import { useApi } from "@/lib/useApi";
import { api, apiErr } from "@/lib/api";
import { useState } from "react";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Switch } from "@/components/ui/switch";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Input } from "@/components/ui/input";
import { PageHeader, EmptyState } from "@/components/common";
import { fDateTime } from "@/lib/format";
import { Plug, RefreshCw, AlertTriangle } from "lucide-react";
import { toast } from "sonner";
import MicrosoftIntegration from "@/components/MicrosoftIntegration";

export default function Integrations() {
  const { data: types } = useApi("/connectors/types");
  const { data: status, reload } = useApi("/integration-status");
  const [type, setType] = useState("");
  const [name, setName] = useState("");

  const add = async () => {
    try { await api.post("/integrations", { connector_type: type, name }); setName(""); setType(""); reload(); toast.success("Integration skapad (mockad)"); }
    catch (e) { toast.error(apiErr(e)); }
  };
  const toggle = async (id) => { try { await api.patch(`/integrations/${id}/toggle`); reload(); } catch (e) { toast.error(apiErr(e)); } };
  const sync = async (id) => { try { const r = await api.post(`/integrations/${id}/sync`); toast.message(r.data.note); reload(); } catch (e) { toast.error(apiErr(e)); } };

  const ints = status?.integrations || [];

  return (
    <div>
      <PageHeader title="Integrationer & synkstatus" icon={Plug}
        subtitle="Read-only som standard. Endast mockade anslutningar tills riktiga behörigheter konfigurerats – ingen riktig SAP-koppling finns." />

      <div className="mb-6"><MicrosoftIntegration /></div>
      <Card className="mb-4 flex flex-wrap items-end gap-3 p-4">
        <div className="min-w-[220px] flex-1">
          <label className="text-xs font-medium text-muted-foreground">Connector-typ</label>
          <Select value={type} onValueChange={setType}>
            <SelectTrigger data-testid="connector-type"><SelectValue placeholder="Välj connector" /></SelectTrigger>
            <SelectContent>{(types || []).map((t) => <SelectItem key={t.id} value={t.id}>{t.name} ({t.category})</SelectItem>)}</SelectContent>
          </Select>
        </div>
        <div className="min-w-[180px] flex-1">
          <label className="text-xs font-medium text-muted-foreground">Namn</label>
          <Input value={name} onChange={(e) => setName(e.target.value)} data-testid="integration-name" />
        </div>
        <Button onClick={add} disabled={!type || !name} data-testid="add-integration">Lägg till</Button>
      </Card>

      <div className="mb-2 flex items-center gap-2 rounded-lg border border-amber-200 bg-amber-50 px-3 py-2 text-xs text-amber-800">
        <AlertTriangle className="h-4 w-4" /> Alla anslutningar är mockade. Systemet låtsas aldrig att en riktig SAP-anslutning finns.
      </div>

      {ints.length === 0 ? <EmptyState icon={Plug} title="Inga integrationer" text="Lägg till en connector-adapter (SAP, EWM, S/4HANA, MES, CSV, webhook)." /> : (
        <div className="grid gap-3 md:grid-cols-2">
          {ints.map((it) => (
            <Card key={it.id} className="p-4" data-testid={`integration-${it.id}`}>
              <div className="flex items-start justify-between">
                <div>
                  <div className="font-head font-semibold">{it.name}</div>
                  <div className="font-mono text-xs text-muted-foreground">{it.connector_type}</div>
                </div>
                <div className="flex items-center gap-2">
                  <span className="text-xs text-muted-foreground">{it.enabled ? "På" : "Av"}</span>
                  <Switch checked={it.enabled} onCheckedChange={() => toggle(it.id)} data-testid={`toggle-${it.id}`} />
                </div>
              </div>
              <div className="mt-3 flex flex-wrap items-center gap-2 text-xs">
                <Badge variant="secondary">{it.status}</Badge>
                <Badge variant="outline">Read-only</Badge>
                <span className="text-muted-foreground">Senast synk: {it.last_sync ? fDateTime(it.last_sync) : "aldrig"}</span>
              </div>
              <Button size="sm" variant="outline" className="mt-3" disabled={!it.enabled} onClick={() => sync(it.id)} data-testid={`sync-${it.id}`}>
                <RefreshCw className="mr-1 h-3.5 w-3.5" />Kör mockad synk
              </Button>
            </Card>
          ))}
        </div>
      )}

      {(status?.recent_logs || []).length > 0 && (
        <Card className="mt-4 p-4">
          <div className="mb-2 text-sm font-semibold">Tekniska loggar</div>
          <ul className="space-y-1 text-xs">
            {status.recent_logs.map((l) => (
              <li key={l.id} className="flex gap-2 font-mono"><span className="text-muted-foreground">{fDateTime(l.timestamp)}</span><span>{l.result}</span><span className="text-muted-foreground">{l.message}</span></li>
            ))}
          </ul>
        </Card>
      )}
    </div>
  );
}
